import { useState } from 'react';
import { ArrowUpCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { updateServiceWorker } from '@services/pwa/updateController';
import { useAppVersion } from '../hooks/useAppVersion';
import { isUpdateAvailable, toDisplayVersion } from '../lib/versionStatus';

/**
 * Non-blocking "a newer version is available" banner driven by the informational
 * policy (C.3). This is the only version UI allowed to use a local build-number
 * comparison, and purely for UX — forced updates come from the middleware (C.5).
 * The action applies the waiting service worker and reloads onto the new build
 * (C.6). Being a *voluntary* update it skips the queue backup and attempt counter
 * that guard the forced flow — nothing is at risk, the queue survives the reload.
 */
export const UpdateAvailableBanner = () => {
  const { t } = useTranslation();
  const { data: policy } = useAppVersion();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !isUpdateAvailable(policy) || !policy) {
    return null;
  }

  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-brand-100 bg-brand-50 px-4 py-2 text-sm text-brand-700">
      <div className="flex items-center gap-2">
        <ArrowUpCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          {t('appVersion.banner.updateAvailable', {
            version: toDisplayVersion(policy.latestVersion)
          })}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="font-semibold underline underline-offset-2"
          onClick={() => void updateServiceWorker(true)}
        >
          {t('appVersion.banner.action')}
        </button>
        <button
          type="button"
          className="rounded p-1 hover:bg-brand-100"
          aria-label={t('appVersion.banner.dismiss')}
          onClick={() => setDismissed(true)}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};
